import {isFeatureEnabled} from 'feature-flags';
import {Form, FormControl, FormSubmitHandler, Option} from 'platform/components';
import {NumberFormatter, useFormatNumber} from 'platform/locale';
import {array, object} from 'yup';

import {ReactNode, useCallback, useState} from 'react';
import {FieldErrors, UseFormReturn} from 'react-hook-form';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  createContext,
  isNotNilOrEmpty,
  useDebouncedCallback,
  yupNumber,
  yupString,
} from 'shared/web';

import {validateVin} from 'features/vin-validate';

import {useCatalogue} from '../../../hooks/useCatalogue';
import i18n from '../../../i18n/i18n';
import {RequestVehicle} from '../../../types/api/api';
import {featureFlags} from '../../../utils/featureFlags';
import {getYearOptions} from '../../../utils/getYearOptions';
import {EXCLUDED_FUEL_TYPES} from '../constants/excludedFuelTypes';
import {MAX_MILEAGE} from '../constants/maxMileage';
import {
  useFilterRelevantOptions,
  useGetRelevantPowerOptions,
} from '../hooks/useFilterRelevantOptions';
import {VehicleFormType} from '../types/VehicleFormType';
import {buildChipsOneOfValidation} from '../utils/buildChipsOneOfValidation';
import {buildFirstRegistrationOnYearValidation} from '../utils/buildFirstRegistrationOnYearValidation';
import {buildPowerValidation} from '../utils/buildPowerValidation';
import {getDefaultValues} from '../utils/getDefaultValues';
import {getFiltersFromFormData} from '../utils/getFiltersFromFormData';

const DEBOUNCED_TIME = 1000;

type AllowedFieldValues = {
  year: number[];
  fuelType: string[];
  transmission: string[];
  drive: string[];
  bodyStyle: string[];
  power: number[];
};

type CreatePageContextType = {
  control: FormControl<VehicleFormType>;
  formApi: UseFormReturn<VehicleFormType>;
  values: VehicleFormType;
  yearOptions: (Option<number> & {suffix?: string})[];
  fuelTypeOptions: (Option<string> & {suffix?: string})[];
  transmissionOptions: (Option<string> & {suffix?: string})[];
  bodyStyleOptions: (Option<string> & {suffix?: string})[];
  driveOptions: (Option<string> & {suffix?: string})[];
  powerOptions: (Option<string> & {suffix?: string})[];
  has: {
    basicInfo: boolean;
    fuelType: boolean;
    transmission: boolean;
    drive: boolean;
    power: boolean;
    bodyStyle: boolean;
  };
  isOptionsLoading: boolean;
};

const [CreatePageContextProvider, useCreatePageContext] = createContext<CreatePageContextType>({});

export const CreatePageContext = (props: {
  children: ReactNode;
  onSubmit: FormSubmitHandler<VehicleFormType>;
  onInvalidSubmit: (errors: FieldErrors<VehicleFormType>) => void;
  onDebouncedChange: (data: VehicleFormType) => void;
  vehicle?: RequestVehicle | null;
  market?: string;
}) => {
  const formatNumber = useFormatNumber();
  const defaultValues = getDefaultValues(props.vehicle);
  // Snapshot of form values kept in sync via onChange; needed here because form state lives inside
  // Form and we need current values in the parent for useGetRelevantPowerOptions and schema.
  const [formData, setFormData] = useState<Partial<VehicleFormType>>(defaultValues ?? {});

  const {
    fuelOptions,
    styleOptions,
    transmissionOptions: allTransmissionOptions,
    driveOptions: allDriveOptions,
    getTransmission,
    getFuelType,
    getDrive,
    getCarStyle,
  } = useCatalogue();

  const debouncedOnChange = useDebouncedCallback(props.onDebouncedChange, DEBOUNCED_TIME);
  const onChange = useCallback(
    (values: VehicleFormType) => {
      setFormData(values);
      debouncedOnChange(values);
    },
    [debouncedOnChange]
  );

  const allowedFuelOptions = fuelOptions?.filter(
    (option) => !EXCLUDED_FUEL_TYPES.includes(option.value)
  );
  const {options: yearOptions, isLoading: isYearOptionsLoading} = useFilterRelevantOptions(
    getYearOptions() ?? [],
    'year',
    getFiltersFromFormData(formData, 'year'),
    props.market
  );
  const {options: fuelTypeOptions, isLoading: isFuelTypeOptionsLoading} = useFilterRelevantOptions(
    allowedFuelOptions ?? [],
    'fuel_type',
    getFiltersFromFormData(formData, 'fuel_type'),
    props.market
  );
  const {options: transmissionOptions, isLoading: isTransmissionOptionsLoading} =
    useFilterRelevantOptions(
      allTransmissionOptions ?? [],
      'transmission',
      getFiltersFromFormData(formData, 'transmission'),
      props.market
    );
  const {options: bodyStyleOptions, isLoading: isBodyStyleOptionsLoading} =
    useFilterRelevantOptions(
      styleOptions ?? [],
      'car_style',
      getFiltersFromFormData(formData, 'car_style'),
      props.market
    );
  const {options: driveOptions, isLoading: isDriveOptionsLoading} = useFilterRelevantOptions(
    allDriveOptions ?? [],
    'drive',
    getFiltersFromFormData(formData, 'drive'),
    props.market
  );
  const {options: powerOptions, isLoading: isPowerOptionsLoading} = useGetRelevantPowerOptions(
    getFiltersFromFormData(formData, 'power'),
    props.market
  );

  const isOptionsLoading =
    isYearOptionsLoading ||
    isFuelTypeOptionsLoading ||
    isTransmissionOptionsLoading ||
    isBodyStyleOptionsLoading ||
    isDriveOptionsLoading ||
    isPowerOptionsLoading;
  const allowedValues: AllowedFieldValues = {
    year: yearOptions.map((option) => option.value),
    fuelType: fuelTypeOptions.map((option) => option.value),
    transmission: transmissionOptions.map((option) => option.value),
    drive: driveOptions.map((option) => option.value),
    bodyStyle: bodyStyleOptions.map((option) => option.value),
    power: powerOptions.map((option) => Number(option.value)),
  };

  const formSchema = (
    formatNumber: NumberFormatter,
    allowed: AllowedFieldValues,
    valueLabelGetters: {
      transmission: (value: string) => string;
      fuelType: (value: string) => string;
      drive: (value: string) => string;
      bodyStyle: (value: string) => string;
    }
  ) =>
    object({
      make: yupString.required(),
      modelFamily: yupString.required(),
      vin: yupString.test('validateVin', i18n.t('page.create.validation.vinIsInvalid'), (vin) =>
        isNilOrEmpty(vin) ? true : validateVin(vin ?? '')
      ),
      firstRegistrationOnYear: buildFirstRegistrationOnYearValidation(allowed.year),
      transmission: buildChipsOneOfValidation(
        allowed.transmission,
        'page.create.validation.transmissionMustBeOneOfOptions',
        valueLabelGetters.transmission
      ),
      fuelType: buildChipsOneOfValidation(
        allowed.fuelType,
        'page.create.validation.fuelTypeMustBeOneOfOptions',
        valueLabelGetters.fuelType
      ),
      bodyStyle: buildChipsOneOfValidation(
        allowed.bodyStyle,
        'page.create.validation.bodyStyleMustBeOneOfOptions',
        valueLabelGetters.bodyStyle
      ),
      drive: buildChipsOneOfValidation(
        allowed.drive,
        'page.create.validation.driveMustBeOneOfOptions',
        valueLabelGetters.drive
      ),
      power: buildPowerValidation(allowed.power),
      exteriorColor: array().of(yupString.required()),
      interiorMaterial: array().of(yupString.required()),
      isVatDeductible: array().of(yupString.required()),
      doorCount: array().of(yupString.required()),
      seatCount: array().of(yupString.required()),
      mileage: yupNumber
        .min(0, i18n.t('page.create.validation.mileageCantBeNegative'))
        .max(MAX_MILEAGE, ({max}) =>
          i18n.t('page.create.validation.mileageMustBeMax', {max: formatNumber(max)})
        )
        .required(),
      features: array().of(yupString.required()),
    });

  const hasBasicInfo =
    isNotNilOrEmpty(formData.make) &&
    isNotNilOrEmpty(formData.modelFamily) &&
    isNotNil(formData.firstRegistrationOnYear) &&
    yearOptions.some((yearOption) => yearOption.value === formData.firstRegistrationOnYear);
  const hasFuelType =
    isNotNilOrEmpty(formData.fuelType) &&
    fuelTypeOptions.some((fuelTypeOption) => fuelTypeOption.value === formData.fuelType?.[0]);
  const hasTransmission =
    isNotNilOrEmpty(formData.transmission) &&
    transmissionOptions.some(
      (transmissionOption) => transmissionOption.value === formData.transmission?.[0]
    );
  const hasDrive =
    isNotNilOrEmpty(formData.drive) &&
    driveOptions.some((driveOption) => driveOption.value === formData.drive?.[0]);
  const hasPower =
    isNotNil(formData.power) &&
    powerOptions.some(
      (powerOption) => isNotNil(formData.power) && powerOption.value === formData.power.toString()
    );
  const hasBodyStyle =
    isNotNilOrEmpty(formData.bodyStyle) &&
    bodyStyleOptions.some((bodyStyleOption) => bodyStyleOption.value === formData.bodyStyle?.[0]);

  const has = {
    basicInfo: !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS) || hasBasicInfo,
    fuelType: !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS) || hasFuelType,
    transmission: !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS) || hasTransmission,
    drive: !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS) || hasDrive,
    power: !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS) || hasPower,
    bodyStyle: !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS) || hasBodyStyle,
  };

  return (
    <Form<VehicleFormType>
      defaultValues={defaultValues}
      schema={formSchema(formatNumber, allowedValues, {
        transmission: getTransmission,
        fuelType: getFuelType,
        drive: getDrive,
        bodyStyle: getCarStyle,
      })}
      onSubmit={props.onSubmit}
      onInvalidSubmit={props.onInvalidSubmit}
      onChange={onChange}
    >
      {(control, formApi) => (
        <CreatePageContextProvider
          value={{
            control,
            formApi,
            values: formApi.getValues(),
            yearOptions,
            fuelTypeOptions,
            transmissionOptions,
            bodyStyleOptions,
            driveOptions,
            powerOptions,
            has,
            isOptionsLoading,
          }}
        >
          {props.children}
        </CreatePageContextProvider>
      )}
    </Form>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export {useCreatePageContext};
